/**
 * Global HostedPCI Message Handler
 * Handles secure communication with HostedPCI iframe for credit card tokenization
 */

// HostedPCI configuration
export const HOSTED_PCI_CONFIG = {
  PROVIDER: "HostedPCI",
  ORIGIN: "https://cci.framehpci.com",
  IFRAME_ID: "ccframe",
  SITE_ID: "534961", // Replace with your actual HostedPCI Site ID
  MESSAGE_TYPES: {
    TOKENIZATION_COMPLETE: "tokenization_complete",
    VALIDATION_ERROR: "validation_error",
    READY: "ready",
  },
  CUSTOM_EVENTS: {
    TOKENIZATION_COMPLETE: "hostedPCITokenizationComplete",
    VALIDATION_ERROR: "hostedPCIValidationError",
    READY: "hostedPCIReady",
  },
  loaded: null,
  ready: false,
  lastTokenization: null, // Track last tokenization to prevent duplicates
};

/**
 * Initialize HostedPCI iframe communication
 * This function is called by the HostedPCI iframe when it loads
 */
window.receiveHPCIMsg = function () {
  if (HOSTED_PCI_CONFIG.loaded) {
    console.warn("HostedPCI iframe already loaded, reinitializing");
  }
  console.log("HostedPCI iframe loaded and ready for communication");
  HOSTED_PCI_CONFIG.loaded = new Date();
  HOSTED_PCI_CONFIG.ready = false; // Will be set to true when ready event is received

  // Set up message listener for iframe communication
  if (window.addEventListener) {
    window.removeEventListener("message", handleHostedPCIMessage, false); // Remove old listener
    window.addEventListener("message", handleHostedPCIMessage, false);
  } else if (window.attachEvent) {
    // IE8 fallback
    window.detachEvent("onmessage", handleHostedPCIMessage);
    window.attachEvent("onmessage", handleHostedPCIMessage);
  }
};

/**
 * Parse query string data from HostedPCI
 * @param {string} queryString - The query string to parse
 * @returns {Object} Parsed object with key-value pairs
 */
function parseHostedPCIData(queryString) {
  const params = new URLSearchParams(queryString);
  const result = {};
  for (const [key, value] of params.entries()) {
    result[key] = value;
  }
  return result;
}

/**
 * Handle messages from HostedPCI iframe
 * @param {MessageEvent} event - The message event from the iframe
 */
function handleHostedPCIMessage(event) {
  console.log("handleHostedPCIMessage:", event);
  // Security check: ensure the message is from the HostedPCI domain
  if (event.origin !== HOSTED_PCI_CONFIG.ORIGIN) {
    console.warn("Ignoring message from unauthorized origin:", event.origin);
    return;
  }

  console.log("Received HostedPCI message:", event.data);

  // Parse the data - HostedPCI sends data as a query string
  let parsedData;
  if (typeof event.data === "string") {
    parsedData = parseHostedPCIData(event.data);
    console.log("Parsed HostedPCI data:", parsedData);
  } else if (event.data && typeof event.data === "object") {
    parsedData = event.data;
  } else {
    console.warn("Unexpected HostedPCI data format:", event.data);
    return;
  }
  // Determine message type based on HostedPCI response fields
  if (parsedData.hpciStatus === "success" && parsedData.hpciCC) {
    // Tokenization complete
    handleTokenizationComplete(parsedData);
  } else if (
    parsedData.hpciStatus === "error" ||
    parsedData.hpciCCValid === "N"
  ) {
    // Validation error
    handleValidationError(parsedData);
  } else if (parsedData.type === HOSTED_PCI_CONFIG.MESSAGE_TYPES.READY) {
    // Ready message (if sent as object)
    handleIframeReady(parsedData);
  } else if (parsedData.hpciCCLast4 || parsedData.hpciCCType || parsedData.hpciCC) {
    // Possible tokenization data without explicit success status
    console.log("Possible tokenization data received:", parsedData);
    handleTokenizationComplete(parsedData);
  } else {
    console.log("Unknown HostedPCI message format:", parsedData);
  }
}

/**
 * Handle successful tokenization completion
 * @param {Object} data - The tokenization data
 */
function handleTokenizationComplete(data) {
  console.log("Credit card tokenization completed successfully");

  // Check for duplicate tokenization (prevent rapid re-submissions)
  const tokenKey = `${data.hpciCC}_${data.hpciCCLast4}_${Date.now()}`;
  const timeSinceLastToken = HOSTED_PCI_CONFIG.lastTokenization 
    ? Date.now() - HOSTED_PCI_CONFIG.lastTokenization 
    : Infinity;
  
  // Prevent duplicate tokenization within 2 seconds
  if (timeSinceLastToken < 2000) {
    console.warn("Duplicate tokenization detected, ignoring");
    return;
  }
  
  HOSTED_PCI_CONFIG.lastTokenization = Date.now();

  // Dispatch custom event for Angular components to listen to
  const tokenizationEvent = new CustomEvent(
    HOSTED_PCI_CONFIG.CUSTOM_EVENTS.TOKENIZATION_COMPLETE,
    {
      detail: data,
    }
  );
  window.dispatchEvent(tokenizationEvent);
}

/**
 * Handle validation errors from HostedPCI
 * @param {Object} data - The error data
 */
function handleValidationError(data) {
  console.warn("HostedPCI validation error:", data);

  // Dispatch custom event for error handling
  const errorEvent = new CustomEvent(
    HOSTED_PCI_CONFIG.CUSTOM_EVENTS.VALIDATION_ERROR,
    {
      detail: data,
    }
  );
  window.dispatchEvent(errorEvent);
}

/**
 * Handle iframe ready state
 * @param {Object} data - The ready state data
 */
function handleIframeReady(data) {
  console.log("HostedPCI iframe is ready for input");
  HOSTED_PCI_CONFIG.ready = true;

  // Dispatch custom event
  const readyEvent = new CustomEvent(HOSTED_PCI_CONFIG.CUSTOM_EVENTS.READY, {
    detail: data,
  });
  window.dispatchEvent(readyEvent);
}

/**
 * Additional HostedPCI helper functions
 */

/**
 * Trigger tokenization of credit card data in the iframe
 * @param {Object} options - Optional configuration for tokenization
 */
window.triggerHPCITokenization = function (options = {}) {
  console.log("Triggering HostedPCI tokenization");

  const iframe = document.getElementById(HOSTED_PCI_CONFIG.IFRAME_ID);
  if (!iframe || !iframe.contentWindow) {
    console.error("HostedPCI iframe not found or not accessible");
    return false;
  }

  try {
    const message = {
      action: "tokenize",
      type: "cc_tokenization_request",
      ...options,
    };

    iframe.contentWindow.postMessage(message, HOSTED_PCI_CONFIG.ORIGIN);
    return true;
  } catch (error) {
    console.error("Failed to send tokenization message:", error);
    return false;
  }
};

/**
 * Check if HostedPCI iframe is loaded and ready
 * @returns {boolean} True if iframe is ready
 */
window.isHostedPCIReady = function () {
  const iframe = document.getElementById(HOSTED_PCI_CONFIG.IFRAME_ID);
  return !!(iframe && iframe.contentWindow);
};

/**
 * Reset the HostedPCI iframe form
 */
window.resetHostedPCIForm = function () {
  console.log("Resetting HostedPCI form");

  const iframe = document.getElementById(HOSTED_PCI_CONFIG.IFRAME_ID);
  if (!iframe || !iframe.contentWindow) {
    console.error("HostedPCI iframe not found");
    return false;
  }

  try {
    iframe.contentWindow.postMessage(
      {
        action: "reset",
        type: "form_reset_request",
      },
      HOSTED_PCI_CONFIG.ORIGIN
    );
    return true;
  } catch (error) {
    console.error("Failed to reset HostedPCI form:", error);
    return false;
  }
};
